import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { BehaviorSubject, filter, pairwise, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { coreSelectors } from 'app/core/state';
import { ModalAddLivestreamComponent } from 'app/shared/modals/add-livestream-modal/add-livestream-modal.component';
import { CurrentView } from 'app/shared/modals/add-livestream-modal/add-livestream-modal.types';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import {
  IncomingMessageType,
  LiveStreamStartedMessage,
  IncomingMessage,
  Presenter,
  StartLiveStreamMessage,
  OutgoingMessageType,
  UnpublishLiveStreamMessage,
  PublishLiveStreamMessage,
  EndLiveStreamMessage,
  UpdateType,
  PresenterType,
  LiveStreamFailedMessage,
  StartMirrorMessage,
  MirrorType,
  EndMirrorMessage,
  MirrorState,
} from 'messages/websocket.messages';

import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { MirrorSubscriber } from '../presentation/mirror-subscriber';
import { MirrorMediaStreams } from '../presentation/presentation.service';
import { LivestreamSubscriberCreator } from '../subscriber/subscriber.creator';
import { SubscriptionReason } from '../subscription/subscription.types';
import { VideoRoomService } from '../video-room.service';
import { WebsocketService } from '../websocket.service';

import { createLiveStreamUrl } from './livestream.helper';
import { LivestreamMediaStreams } from './livestream.types';

@Injectable({
  providedIn: 'root',
})
export class LivestreamService extends Loggable {
  private dialog = inject(MatDialog);
  private websocketService = inject(WebsocketService);
  private videoRoomService = inject(VideoRoomService);
  private store = inject(Store);

  public isPreview?: boolean;
  public livestreamMediaStream$ = new BehaviorSubject<LivestreamMediaStreams>({});
  public livestreamPresenters: User[] = [];

  private activeLivestreamId?: string;
  private previewLivestreamId?: string;
  private livestreamSubscribers = new Map<string, LivestreamSubscriberCreator>();
  private livestreamMediaStreams: LivestreamMediaStreams = {};
  private userId?: string;
  private isCrossRoomPresentation = false;
  private previewFailed = false;
  private socialId?: string;
  private presenters?: Record<string, Presenter>;
  private isPresentationActive = false;

  // Mirror State
  private mirrorId?: string;
  private mirrors: MirrorState[] = [];
  private mirrorMediaStreamSource = new BehaviorSubject<MirrorMediaStreams>({});
  private mirrorSubscribers = new Map<string, MirrorSubscriber>();

  public constructor() {
    super();
    this.setDisplayName('LivestreamService');
    this.websocketService.message$.subscribe(this.handleMessage.bind(this));

    this.store.select(coreSelectors.getUserId).subscribe((userId) => (this.userId = userId));

    this.store
      .select(socialAppSelectors.getJoinedSocialId)
      .pipe(
        pairwise(),
        filter(([prev, next]) => prev !== next && !isNil(prev)),
      )
      .subscribe(() => this.destroyMirrorSubscribers());

    this.store.select(socialAppSelectors.getPresentation).subscribe((presentation) => {
      this.isPresentationActive = !!presentation;
      this.presenters = presentation?.presenters ?? {};
    });

    this.store.select(socialAppSelectors.getActiveLivestreamsIds).subscribe((activeLivestreamsIds) => {
      this.activeLivestreamId = activeLivestreamsIds[0];
      if (this.mirrors.length) {
        this.mirrors.forEach((m) => {
          const mirror = this.mirrorSubscribers.get(m.mirrorId);
          if (m.mirrorId.includes('livestream')) {
            if (!mirror) {
              const { audio, mirrorId, publishPeerTime, video } = m;
              const subscriber = new MirrorSubscriber(
                this.videoRoomService,
                this.websocketService,
                this.store,
                mirrorId,
                publishPeerTime,
                audio,
                video,
                SubscriptionReason.LIVESTREAM,
                this.onMediaStreamChange,
              );
              this.mirrorSubscribers.set(mirrorId, subscriber);
            }
          }
        });
      }
    });

    this.store.select(socialAppSelectors.getActiveLivestreams).subscribe((streams) => {
      this.livestreamPresenters = streams ?? [];
    });

    this.store.select(socialAppSelectors.isLivestreamCrossRoom).subscribe((isCrossRoom) => {
      if (isCrossRoom && this.activeLivestreamId && !this.mirrorId) {
        const mirror = this.mirrorSubscribers.get(this.activeLivestreamId);
        if (mirror) {
          this.mirrorId = this.activeLivestreamId;
          return;
        }
        this.startMirror(this.activeLivestreamId);
      }
    });

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(pairwise())
      .subscribe(([previousIsCrossRoom, isCrossRoom]) => {
        this.isCrossRoomPresentation = isCrossRoom;
        if (previousIsCrossRoom && !isCrossRoom && this.mirrorId) {
          this.endMirror();
        }
      });
  }

  public openAddLivestreamModal(view: CurrentView, livestream?: Presenter): void {
    this.dialog.open<ModalAddLivestreamComponent>(ModalAddLivestreamComponent, {
      width: '520px',
      panelClass: 'mat-dialog-custom-container',
      data: { currentView: view, livestream: livestream },
      disableClose: true,
    });
  }

  public addLivestreamToQueue({
    createUrl,
    labelStream,
    socialId,
    streamId,
    streamKey,
    streamUrl,
  }: {
    createUrl?: boolean;
    labelStream: string;
    streamUrl: string;
    streamKey?: string;
    streamId: string;
    socialId: string;
  }): void {
    const description = createUrl ? createLiveStreamUrl(streamUrl, streamKey) : streamUrl;
    const livestream: Presenter = {
      userId: streamId,
      isGuest: false,
      description,
      firstName: labelStream,
      presenterType: PresenterType.LIVE_STREAM,
      socialId,
    };
    this.info('Add Livestream to Queue', { livestream });
    SocialStateMachine.shared
      .requestAddLivestreamToQueue({
        livestream,
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => {
        this.error('Failed to add livestream to queue', e);
      });
  }

  public removeLivestreamFromQueue(streamId: string): void {
    this.info('Remove Livestream from Queue', streamId);

    if (!streamId) {
      this.warn('Cannot remove livestream from queue, streamId undefined');
      return;
    }

    SocialStateMachine.shared
      .requestRemoveLivestreamFromQueue({
        streamId,
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => {
        this.error('Failed to remove livestream from queue', e);
      });
  }

  public async moveLivestreamBackToQueue(livestream: Presenter): Promise<void> {
    this.info('Add Livestream back to the Queue', { livestream });

    if (!livestream) {
      this.warn('Cannot move livestream back to queue, livestream undefined');
      return;
    }

    await this.removeActiveLivestream(livestream).catch((e) => this.error('Failed to remove active livestream', e));
    SocialStateMachine.shared
      .requestAddLivestreamToQueue({
        livestream,
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => {
        this.error('Failed to move livestream back to queue', e);
      });
  }

  public addActiveLivestream(livestream: Presenter, crossRoom: boolean): void {
    this.info('Add Active Livestream to Presentation', livestream);

    if (!livestream) {
      this.warn('Cannot add livestream to presentation, livestream undefined');
      return;
    }

    const { userId: streamId } = livestream;
    const userId = this.userId ?? '';

    this.removeLivestreamFromQueue(streamId);

    SocialStateMachine.shared
      .requestAddActivePresenter({
        presenter: livestream,
        updateType: crossRoom ? UpdateType.ALL : UpdateType.CURRENT,
        userId,
      })
      .catch((e) => {
        this.error('Failed to add active livestream', e);
      });

    this.publishLiveStream(livestream);
  }

  /*
   * Remove active livestream from some number of rooms and optionally unpublish it
   * @param {Presenter} livestream - livestream to remove
   * @param {boolean} preserveLivestream - if true, the livestream will not be removed from the room
   * @returns {void}
   * @private
   * @memberof LivestreamService
   * @example
   * removeActiveLivestream(livestream, true);
   * // livestream will be removed from cross-room and not unpublished
   * removeActiveLivestream(livestream);
   * // livestream will be removed from all rooms and unpublished
   * */
  public async removeActiveLivestream(livestream: Presenter, preserveLivestream = false): Promise<boolean> {
    if (!this.isPresentationActive) {
      this.warn('Cannot remove livestream, presentation is not active', { livestream });
      return false;
    }

    if (!livestream) {
      this.warn('Cannot remove livestream, livestream undefined');
      return false;
    }

    this.info('Remove Active Livestream from Presentation', { livestream });
    const userId = this.userId ?? '';
    const streamId = livestream.userId;

    if (this.mirrorId) {
      this.endMirror();
    }

    if (!preserveLivestream) {
      this.removeLivestreamSubscriber(streamId);
      this.unPublishLiveStream(livestream);
    }

    // Remove livestream from presentation
    await SocialStateMachine.shared
      .requestRemoveActivePresenter({
        presenterId: streamId,
        updateType: this.isCrossRoomPresentation && !preserveLivestream ? UpdateType.ALL : UpdateType.CURRENT,
        userId,
      })
      .catch((e) => {
        this.error('Failed to remove active livestream', e);
      });
    return true;
  }

  public unSubscribeLivestream(streamId: string): void {
    this.info(`UnSubscribe to livestream for ${streamId}`);
    const livestream = this.livestreamSubscribers.get(streamId);
    if (!livestream) {
      this.warn('Cannot unsubscribe to livestream, stream undefined');
      return;
    }
    livestream.unsubscribe();
  }

  public subscribeLivestream(streamId: string): void {
    this.info(`Subscribe to livestream for ${streamId}`);
    const livestream = this.livestreamSubscribers.get(streamId);
    if (!livestream) {
      this.warn('Cannot subscribe to livestream, stream undefined');
      return;
    }
  }

  public async restartLivestreamPreview(streamId: string, url: string | undefined): Promise<string | void> {
    this.info(`Subscribe to livestream for ${streamId}`);
    const livestream = this.livestreamSubscribers.get(streamId);
    if (livestream) {
      livestream.subscribe();
      return streamId;
    } else {
      if (url) {
        const newStreamId = await this.startLiveStream(url);
        return newStreamId;
      } else {
        this.warn('Cannot preview livestream, url undefined');
      }
    }
  }

  private startMirror(streamId: string): void {
    this.info('Starting livestream mirror session', { mirrorId: streamId });

    if (!streamId) {
      this.warn('Attempted to start mirror with no livestream');
      return;
    }

    const livestream = Object.values(this.presenters ?? {}).filter((p) => p.userId === streamId);
    const socialId = livestream[0].socialId;
    if (!this.socialId) {
      this.warn('Social id is undefined');
    }

    try {
      const msg: StartMirrorMessage = {
        t: OutgoingMessageType.START_MIRROR,
        mirrorId: streamId,
        type: MirrorType.LIVE_STREAM,
        socialId: socialId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to start livestream mirror session:', e);
    }

    this.mirrorId = streamId;
    this.socialId = socialId;
  }

  private endMirror(): void {
    if (!this.mirrorId) {
      this.warn('Attempted to end mirror with no mirror id');
      return;
    }

    this.info('Ending livestream mirror session', { mirrorId: this.mirrorId });
    try {
      const msg: EndMirrorMessage = {
        t: OutgoingMessageType.END_MIRROR,
        mirrorId: this.mirrorId,
        type: MirrorType.LIVE_STREAM,
        socialId: this.socialId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to end livestream mirror session:', e);
    }

    this.mirrorId = undefined;
    this.socialId = undefined;
  }

  public publishLiveStream(livestream: Presenter): void {
    if (!livestream) {
      this.warn('Cannot publish livestream, stream is undefined');
      return;
    }
    const streamId = livestream.userId;
    const socialId = livestream.socialId;

    this.info(`Publishing livestream session for ${streamId}`);

    try {
      const msg: PublishLiveStreamMessage = {
        t: OutgoingMessageType.LS_PUB,
        streamId,
        socialId: socialId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to start livestream session:', e);
    }

    if (this.isCrossRoomPresentation) {
      this.startMirror(streamId);
    }
  }

  public unPublishLiveStream(livestream: Presenter): void {
    if (!livestream) {
      this.warn('Cannot unpublish livestream, stream is undefined');
      return;
    }

    const socialId = livestream.socialId;
    const streamId = livestream.userId;

    this.info(`UnPublishing livestream session for ${streamId}`);

    try {
      const msg: UnpublishLiveStreamMessage = {
        t: OutgoingMessageType.LS_UNPUB,
        streamId,
        socialId: socialId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to unpublish livestream session:', e);
    }
  }

  public startLiveStream(url: string): Promise<string> {
    this.info(`Creating livestream session for ${url}`);
    this.previewFailed = false;
    return new Promise((resolve, reject) => {
      try {
        const msg: StartLiveStreamMessage = {
          t: OutgoingMessageType.LS_START,
          // FIXME: pass and save here so that it can be matched with failed or started message.
          streamId: 'livestream-' + new Date().getTime(),
          url,
        };
        this.websocketService.sendMessage(msg);
        this.websocketService.message$
          .pipe(
            filter((m) => m.t === IncomingMessageType.LS_STARTED || m.t === IncomingMessageType.LS_FAILED),
            take(1),
          )
          .subscribe((m) => {
            if (m.t === IncomingMessageType.LS_STARTED) {
              const { streamId } = m as LiveStreamStartedMessage;
              resolve(streamId);
            }
            if (m.t === IncomingMessageType.LS_FAILED) {
              const { error } = m as LiveStreamFailedMessage;
              reject(error);
            }
          });
      } catch (e) {
        const { message } = e as { message: string };
        reject(`Failed to start livestream session, ${message}`);
      }
    });
  }

  public endLiveStream(streamId: string): void {
    this.info(`Ending livestream session for ${streamId}`);
    try {
      const msg: EndLiveStreamMessage = {
        t: OutgoingMessageType.LS_END,
        streamId,
      };
      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to end livestream session', e);
    }
  }

  private handleMessage = async (msg: IncomingMessage): Promise<void> => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.LS_STARTED: {
        // Preview livestream has started.
        const { streamId, userId } = msg as LiveStreamStartedMessage;
        if (userId === this.userId && this.previewFailed === false) {
          this.isPreview = true;
          this.previewLivestreamId = streamId;
          this.createLivestreamSubscriber(streamId);
        }
        break;
      }
      case IncomingMessageType.LS_PUBLISHED: {
        // Livestream has been published and everyone in the room can see it.
        const { streamId } = msg;
        const livestream = this.livestreamSubscribers.get(streamId);
        if (livestream) {
          livestream.subscribe();
        } else {
          this.createLivestreamSubscriber(streamId);
        }
        this.isPreview = false;
        this.activeLivestreamId = streamId;
        break;
      }
      case IncomingMessageType.LS_OFFER: {
        const { d: offer, peerTime } = msg;
        const streamId = this.isPreview ? this.previewLivestreamId : this.activeLivestreamId;
        if (!streamId) {
          return;
        }
        const livestream = this.livestreamSubscribers.get(streamId);
        if (!livestream) {
          this.warn(`Livestream ${streamId} not found`);
          return;
        }
        livestream.subscriber?.addOffer(offer, peerTime).catch((e) => {
          this.error('Failed to add offer to livestream', e);
        });
        break;
      }

      case IncomingMessageType.LS_ENDED: {
        // Preview livestream has ended
        const { streamId } = msg;
        this.removeLivestreamSubscriber(streamId);
        break;
      }

      case IncomingMessageType.LS_UNPUBLISHED: {
        // Livestream has ended for everyone in the room
        const { streamId } = msg;
        this.removeLivestreamSubscriber(streamId);
        break;
      }

      case IncomingMessageType.LS_FAILED: {
        // Livestream fails to set up
        // ! This should probably be on the preview modal during livestream set up
        this.previewFailed = true;
        const { streamId } = msg;
        this.removeLivestreamSubscriber(streamId);
        break;
      }

      case IncomingMessageType.INIT_STATE: {
        this.mirrors = msg.mirrors ?? [];
        msg.mirrors?.forEach((mirror) => {
          const m = this.mirrorSubscribers.get(mirror.mirrorId);
          if (mirror.mirrorId.includes('livestream')) {
            if (!m) {
              const { audio, mirrorId, publishPeerTime, video } = mirror;
              const subscriber = new MirrorSubscriber(
                this.videoRoomService,
                this.websocketService,
                this.store,
                mirrorId,
                publishPeerTime,
                audio,
                video,
                SubscriptionReason.LIVESTREAM,
                this.onMediaStreamChange,
              );
              this.mirrorSubscribers.set(mirrorId, subscriber);
            }
          }
        });
        break;
      }

      case IncomingMessageType.MIRROR_STARTED: {
        const { audio, mirrorId, mirrorPeerTime, video } = msg;
        const m = this.mirrorSubscribers.get(mirrorId);
        if (mirrorId.includes('livestream')) {
          if (!m) {
            const subscriber = new MirrorSubscriber(
              this.videoRoomService,
              this.websocketService,
              this.store,
              mirrorId,
              mirrorPeerTime,
              audio,
              video,
              SubscriptionReason.LIVESTREAM,
              this.onMediaStreamChange,
            );
            this.mirrorSubscribers.set(mirrorId, subscriber);
          }
        }
        break;
      }

      case IncomingMessageType.MIRROR_HUP: {
        const { mirrorId } = msg;
        if (mirrorId.includes('livestream')) {
          this.removeMirror(mirrorId);
        }
        break;
      }

      case IncomingMessageType.MIRROR_OFFER: {
        const { d: offer, mirrorId, pt: peerTime } = msg;
        const mirrorSubscriber = this.mirrorSubscribers.get(mirrorId);
        if (mirrorId.includes('livestream')) {
          if (!mirrorSubscriber) {
            this.warn('Received mirror offer message for unknown mirror', { mirrorId });
            return;
          }
          await mirrorSubscriber.addOffer(offer, peerTime);
        }
        break;
      }

      case IncomingMessageType.MIRROR_STATE: {
        const { mirrorId } = msg;
        const mirrorSubscriber = this.mirrorSubscribers.get(mirrorId);
        if (mirrorId.includes('livestream')) {
          if (!mirrorSubscriber) {
            this.warn('Received mirror state message for unknown mirror', { mirrorId });
            return;
          }
          mirrorSubscriber.handleMirrorState(msg);
        }
        break;
      }
    }
  };

  public removeLivestreamSubscriber(streamId: string): void {
    this.info('Removing livestream subscriber', { streamId });
    const livestream = this.livestreamSubscribers.get(streamId);
    if (!livestream) {
      return;
    }
    this.livestreamSubscribers.delete(streamId);
    livestream?.subscriber?.destroy();
  }

  private createLivestreamSubscriber(streamId: string): void {
    this.info('Creating livestream subscriber', { streamId });
    const livestream = this.livestreamSubscribers.get(streamId);
    if (!livestream) {
      const peerTime = new Date();
      const livestreamCreator = new LivestreamSubscriberCreator(
        this.videoRoomService,
        this.websocketService,
        this.store,
        streamId,
        peerTime.getTime(),
        true,
        true,
        this.onMediaStreamChange,
      );
      livestreamCreator.subscribe();
      this.livestreamSubscribers.set(streamId, livestreamCreator);
    }
  }

  // Destroy any existing mirror subscribers
  private destroyMirrorSubscribers(): void {
    this.info('Destroying presentation mirror subscribers');
    this.mirrorSubscribers.forEach((subscriber) => subscriber.destroy());
    this.mirrorSubscribers.clear();
    this.mirrors = [];
  }

  /**
   *  Remove subscriptions from a mirror subscriber
   * @param presenter The presenter to remove
   */
  private removeMirror(mirrorId: string): void {
    this.info('Removing mirror', { mirrorId });
    const subscriber = this.mirrorSubscribers.get(mirrorId);
    subscriber?.destroy();
    this.mirrorSubscribers.delete(mirrorId);
    this.store.dispatch(socialAppActions.REMOVE_PARTICIPANT({ userId: mirrorId }));
  }

  /**
   * Handle media stream changes from livestream peers
   */
  private onMediaStreamChange = (livestreamId: string, audio?: MediaStream, video?: MediaStream): void => {
    const audioId = uuidv4();
    const videoId = uuidv4();

    this.livestreamMediaStreams[livestreamId] = { audio, videoId, video, audioId };
    this.livestreamMediaStream$.next(this.livestreamMediaStreams);
  };
}
